/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Catalog recommendations", description = "Generate personalized product recommendations for every user from any item catalog in your data warehouse.", lastUpdatedDate = new Date(1788480000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Catalog recommendations",
        description: "Generate personalized product recommendations for every user from any item catalog in your data warehouse.",
        lastUpdatedDate: "Fri Sep 04 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How it works",
          "depth": 1,
          "id": "how-it-works"
        }, {
          "value": "Use cases",
          "depth": 1,
          "id": "use-cases"
        }, {
          "value": "Set up a catalog",
          "depth": 0,
          "id": "set-up-a-catalog"
        }, {
          "value": "Create a catalog recommendation",
          "depth": 0,
          "id": "create-a-catalog-recommendation"
        }, {
          "value": "Filter the catalog",
          "depth": 1,
          "id": "filter-the-catalog"
        }, {
          "value": "Filter eligible users",
          "depth": 1,
          "id": "filter-eligible-users"
        }, {
          "value": "Set a suppression filter",
          "depth": 1,
          "id": "set-a-suppression-filter"
        }, {
          "value": "Set the update schedule",
          "depth": 1,
          "id": "set-the-update-schedule"
        }, {
          "value": "Evaluate recommendation quality",
          "depth": 0,
          "id": "evaluate-recommendation-quality"
        }, {
          "value": "Hit Rate@20",
          "depth": 1,
          "id": "hit-rate20"
        }, {
          "value": "NDCG@20",
          "depth": 1,
          "id": "ndcg20"
        }, {
          "value": "Average position of first hit",
          "depth": 1,
          "id": "average-position-of-first-hit"
        }, {
          "value": "Use catalog recommendations",
          "depth": 0,
          "id": "use-catalog-recommendations"
        }, {
          "value": "Sync recommendations to a destination",
          "depth": 1,
          "id": "sync-recommendations-to-a-destination"
        }, {
          "value": "Build audiences from recommendations",
          "depth": 1,
          "id": "build-audiences-from-recommendations"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      code: "code",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A defined ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "schema"
                  }), " with at least one parent model (for example, ", _jsx(_components.code, {
                    children: "Users"
                  }), ")."]
                }), _jsx("li", {
                  children: "A catalog of items (products, offers, digital content, etc.) represented as a table in your warehouse."
                }), _jsx("li", {
                  children: "Event data linking users to catalog items (for example, purchases, add-to-cart events, email clicks)."
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Catalog recommendations use machine learning to generate the top recommended items from any catalog for each user, so you can personalize syncs and audience targeting at scale."
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Coming from AI Decisioning Collections?"
          }), " Managing catalog filters from the\nAI Decisioning sidebar is deprecated. Catalog recommendations are the current\nway to generate personalized product recommendations from your warehouse\ncatalogs. Using them within AI Decisioning isn't fully self-serve yet.", " ", "\n", _jsx(PylonLink, {
            children: "Contact us"
          }), " before you set it up."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Standard traits like counts and aggregations tell you what users have done. Catalog recommendations go further by predicting which items each user is most likely to engage with next."
      }), "\n", _jsx(_components.p, {
        children: "Given a catalog of any size — hundreds to hundreds of thousands of items — Hightouch trains a model on your historical interaction data and generates a ranked list of the top 20 recommended items per user. You can then sync those recommendations to any destination or use them to build highly targeted audiences."
      }), "\n", _jsxs(_components.p, {
        children: ["Catalog recommendations are part of the ", _jsx(_components.a, {
          href: "/customer-studio/predictive-traits",
          children: "predictive traits"
        }), " suite and share the same ML infrastructure."]
      }), "\n", _jsx(_components.h3, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch uses three inputs to train the recommendation model:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Users"
          }), " — the parent model in your schema (for example, ", _jsx(_components.code, {
            children: "Users"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Catalog"
          }), " — a table of items in your warehouse (for example, ", _jsx(_components.code, {
            children: "Products"
          }), ", ", _jsx(_components.code, {
            children: "Offers"
          }), ", ", _jsx(_components.code, {
            children: "Listings"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Events"
          }), " — interactions between users and catalog items (for example, purchases, add-to-cart events, page views, email clicks)."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Each time the model runs, Hightouch pulls training data from your warehouse, trains the model, and writes recommendations back. The model generates a ranked list of the top 20 items for each user based on their predicted propensity toward each item."
      }), "\n", _jsx(_components.h3, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Personalized product syncs"
          }), " — Sync each user's top recommended products to an ESP or ad platform by mapping the catalog recommendation trait as a sync field, then use the ranked product array to populate personalized email templates or dynamic ad creative."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Targeted audience building"
          }), " — Create an audience in Customer Studio filtered by catalog recommendation trait (for example, users whose top five include a specific product), then sync that audience to an ads platform to run a targeted launch campaign for that product line."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-a-catalog",
        children: "Set up a catalog"
      }), "\n", _jsx(_components.p, {
        children: "Before you create a catalog recommendation trait, you need a catalog configured for recommendations in your schema."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Schema"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Add or select the table that represents your catalog (for example, ", _jsx(_components.code, {
            children: "Products"
          }), "). The catalog must be connected to a parent model through event or relationship tables."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the catalog model's detail page, go to the ", _jsx(_components.strong, {
            children: "Recommendations"
          }), " tab and enable ", _jsx(_components.strong, {
            children: "catalog recommendations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "parent model"
          }), " that represents your users."]
        }), "\n", _jsxs(_components.li, {
          children: ["Define the ", _jsx(_components.strong, {
            children: "key events"
          }), " that capture how users interact with catalog items (for example, ", _jsx(_components.code, {
            children: "AddToCart"
          }), ", ", _jsx(_components.code, {
            children: "Purchase"
          }), ", ", _jsx(_components.code, {
            children: "EmailClick"
          }), "). These events are what the model uses to learn user preferences."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/schema-create-catalog.png",
          alt: "Schema page showing a catalog connected to a parent model through event tables"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "You can use multiple event types to train a single recommendation model. For\nexample, combining purchase events with add-to-cart events gives the model\nmore signal about user preferences."
        })
      }), "\n", _jsx(_components.h2, {
        id: "create-a-catalog-recommendation",
        children: "Create a catalog recommendation"
      }), "\n", _jsx(_components.p, {
        children: "To create a catalog recommendation:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Traits"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create > New trait"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Method"
          }), " step, select a ", _jsx(_components.strong, {
            children: "parent model"
          }), " (for example, ", _jsx(_components.code, {
            children: "Users"
          }), ") that has a relationship with the catalog you want to generate recommendations for."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Calculation method"
          }), ", select ", _jsx(_components.strong, {
            children: "Catalog Recommendation"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/new-trait-catalog-rec.png",
          alt: "Method step with Catalog Recommendation selected"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "6",
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Calculation"
          }), " step, select the ", _jsx(_components.strong, {
            children: "catalog"
          }), " you want to generate recommendations from. The remaining settings on this page are optional."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/calculate-trait-catalog.png",
          alt: "Calculation step showing catalog selection, filters, eligible users, and update schedule"
        })
      }), "\n", _jsx(_components.h3, {
        id: "filter-the-catalog",
        children: "Filter the catalog"
      }), "\n", _jsx(_components.p, {
        children: "You can optionally add filters to specify which items to include from your catalog. Filters use columns from the catalog table."
      }), "\n", _jsx(_components.p, {
        children: "For example, if your catalog contains all products but you only want to recommend shoes, you can add a filter where the product name contains \"shoes\" or the category equals \"footwear.\""
      }), "\n", _jsx(_components.h3, {
        id: "filter-eligible-users",
        children: "Filter eligible users"
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Who should this prediction apply to?"
        }), ", you can filter which users receive recommendations. By default, every user in your parent model is scored. To narrow the pool, add filters based on something users have already done — for example, signing up, viewing a page, or starting checkout."]
      }), "\n", _jsx(_components.h3, {
        id: "set-a-suppression-filter",
        children: "Set a suppression filter"
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally, enable the ", _jsx(_components.strong, {
          children: "suppression filter"
        }), " to suppress recommendations for items that a user has already interacted with. When enabled, you select the event that represents the interaction (for example, a purchase event), and the model excludes those items from that user's recommendations."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/catalog-suppression-filter.png",
          alt: "Suppression filter enabled with an event selected"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-the-update-schedule",
        children: "Set the update schedule"
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "How often should scores be updated?"
        }), ", choose how often the model should retrain and refresh recommendations. Each run uses warehouse compute to pull data and write results, so the schedule affects warehouse usage. The model generates 20 recommended items per user on each run."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "More frequent updates keep recommendations fresh but increase warehouse usage.\nLess frequent updates are appropriate when your catalog and user behavior\nchange slowly."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After configuring the calculation, click ", _jsx(_components.strong, {
          children: "Continue to finalize"
        }), ". In the ", _jsx(_components.strong, {
          children: "Finalize"
        }), " step, name your trait, optionally add a description, review the calculation summary, and click ", _jsx(_components.strong, {
          children: "Create trait"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/finalize-trait-catalog.png",
          alt: "Finalize step showing trait name, description, and calculation summary"
        })
      }), "\n", _jsx(_components.p, {
        children: "The model begins training immediately. Training time varies based on the size of your data."
      }), "\n", _jsx(_components.h2, {
        id: "evaluate-recommendation-quality",
        children: "Evaluate recommendation quality"
      }), "\n", _jsxs(_components.p, {
        children: ["After the model completes training, the ", _jsx(_components.strong, {
          children: "Prediction analysis"
        }), " tab on the trait detail page displays performance metrics. The page shows the total number of users and items evaluated, along with the last run date."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/catalog-prediction-analysis-2.png",
          alt: "Prediction analysis tab showing Hit Rate@20, NDCG@20, average position of first hit, and Model vs Random Baseline comparison"
        })
      }), "\n", _jsx(_components.h3, {
        id: "hit-rate20",
        children: "Hit Rate@20"
      }), "\n", _jsxs(_components.p, {
        children: ["Hit Rate@20 measures the fraction of users where at least one relevant item appeared in the top 20 recommendations. A higher hit rate is better. The analysis page also shows a ", _jsx(_components.strong, {
          children: "Model vs Random Baseline"
        }), " comparison that visualizes the model's hit rate against what you'd expect from randomly assigning items — this lift metric helps you gauge how much value the model adds."]
      }), "\n", _jsx(_components.h3, {
        id: "ndcg20",
        children: "NDCG@20"
      }), "\n", _jsx(_components.p, {
        children: "NDCG (Normalized Discounted Cumulative Gain) at 20 measures recommendation ranking quality, accounting for position. Values closer to 1 indicate better ranking — meaning items the user is most likely to engage with are placed closer to the top of the list."
      }), "\n", _jsx(_components.h3, {
        id: "average-position-of-first-hit",
        children: "Average position of first hit"
      }), "\n", _jsx(_components.p, {
        children: "Among users who received a relevant recommendation, this metric shows the average rank position where the first relevant item appears. A lower number means the model tends to place relevant items near the top of the list."
      }), "\n", _jsx(_components.h2, {
        id: "use-catalog-recommendations",
        children: "Use catalog recommendations"
      }), "\n", _jsx(_components.p, {
        children: "Once a catalog recommendation trait is created and trained, you can use it in two ways."
      }), "\n", _jsx(_components.h3, {
        id: "sync-recommendations-to-a-destination",
        children: "Sync recommendations to a destination"
      }), "\n", _jsx(_components.p, {
        children: "When you set up a sync, you can select the catalog recommendation trait as a field to include. Recommendations sync as a ranked array of each user's top recommended items, so you can power personalized content in downstream tools such as email templates, push notifications, and ad creative."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Syncs"
          }), " and create or edit a sync."]
        }), "\n", _jsx(_components.li, {
          children: "In the field mapping step, select your catalog recommendation trait."
        }), "\n", _jsx(_components.li, {
          children: "The recommendation data syncs as a structured array to your destination."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/sync-field-mapping-catalog-rec.png",
          alt: "Sync field mapping with a catalog recommendation trait mapped to a destination field"
        })
      }), "\n", _jsx(_components.h3, {
        id: "build-audiences-from-recommendations",
        children: "Build audiences from recommendations"
      }), "\n", _jsx(_components.p, {
        children: "You can use catalog recommendations to define audience membership. For example, to target users who are predicted to be interested in a specific product:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Audiences"
          }), " and create a new audience."]
        }), "\n", _jsx(_components.li, {
          children: "Add a filter using your catalog recommendation trait."
        }), "\n", _jsx(_components.li, {
          children: "Set a percentile range or filter by specific items to define which users qualify for the audience."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/catalog-recommendations/catalog-rec-trait-audience-filter.png",
          alt: "Audience builder with a catalog recommendation trait filter"
        })
      }), "\n", _jsx(_components.p, {
        children: "This lets you build launch audiences, cross-sell segments, or personalized campaign groups based on predicted product affinity rather than past behavior alone."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " to filter users by their recommended items in the visual builder."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "Sync audiences"
          }), " to send each user's ranked recommendations to a destination."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/predictive-traits",
            children: "Predictive traits"
          }), " to score the likelihood of a specific future action."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/lookalike-audiences",
            children: "Lookalike audiences"
          }), " to find users who resemble a high-value segment."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
